import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from 'sonner';

// ========== LAZY LOADING - CODE SPLITTING POR ROTA ==========
// Carrega componentes apenas quando necessário, reduzindo bundle inicial ~60%

// Página de login carregada estaticamente (entrada principal)
import Login from '@/pages/Login';

// Todas as outras páginas com lazy loading
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Projects = lazy(() => import('@/pages/Projects'));
const PlanningOverview = lazy(() => import('@/pages/PlanningOverview'));
const ProjectForm = lazy(() => import('@/pages/ProjectForm'));
const ProjectDetail = lazy(() => import('@/pages/ProjectDetail'));
const Production = lazy(() => import('@/pages/Production'));
const Stages = lazy(() => import('@/pages/Stages'));
const OrderTypes = lazy(() => import('@/pages/OrderTypes'));
const Partners = lazy(() => import('@/pages/Partners'));
const Suppliers = lazy(() => import('@/pages/Suppliers'));
const Checkpoints = lazy(() => import('@/pages/Checkpoints'));
const Rules = lazy(() => import('@/pages/Rules'));
const AdvancedRules = lazy(() => import('@/pages/AdvancedRules'));
const RuleExecutionLogs = lazy(() => import('@/pages/RuleExecutionLogs'));
const Alerts = lazy(() => import('@/pages/Alerts'));
const Users = lazy(() => import('@/pages/Users'));
const CustomListings = lazy(() => import('@/pages/CustomListings'));
const Planning = lazy(() => import('@/pages/Planning'));
const ProductionPlanning = lazy(() => import('@/pages/ProductionPlanning'));
const Capacity = lazy(() => import('@/pages/Capacity'));
const Brands = lazy(() => import('@/pages/Brands'));
const ConfeccaoPlanning = lazy(() => import('@/pages/ConfeccaoPlanning'));
const ConfeccaoPerformance = lazy(() => import('@/pages/ConfeccaoPerformance'));
const Lavandaria = lazy(() => import('@/pages/Lavandaria'));
const AcabamentosPlanning = lazy(() => import('@/pages/AcabamentosPlanning'));
const AcabamentosPerformance = lazy(() => import('@/pages/AcabamentosPerformance'));
const AcabamentosCapacity = lazy(() => import('@/pages/AcabamentosCapacity'));
const QualityControlDashboard = lazy(() => import('@/pages/QualityControlDashboard'));
const Reports = lazy(() => import('@/pages/Reports'));
const ReportsConfeccao = lazy(() => import('@/pages/ReportsConfeccao'));
const ReportsAcabamentos = lazy(() => import('@/pages/ReportsAcabamentos'));
const Permissions = lazy(() => import('@/pages/Permissions'));
const OperatorDashboard = lazy(() => import('@/pages/OperatorDashboard'));
const ListView = lazy(() => import('@/pages/ListView'));
const Agenda = lazy(() => import('@/pages/Agenda'));
const QualityControlMobile = lazy(() => import('@/pages/QualityControlMobile'));
const QCLogin = lazy(() => import('@/pages/QCLogin'));
const ProjectSimulation = lazy(() => import('@/pages/ProjectSimulation'));
const FieldsConfig = lazy(() => import('@/pages/FieldsConfig'));
const AcabamentosSimulation = lazy(() => import('@/pages/AcabamentosSimulation'));
const EmailSettings = lazy(() => import('@/pages/EmailSettings'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const ChangePassword = lazy(() => import('@/pages/ChangePassword'));
const Reservas = lazy(() => import('@/pages/Reservas'));
const CuttingSimulator = lazy(() => import('@/pages/CuttingSimulator'));

// Loading fallback otimizado
const PageLoader = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="flex flex-col items-center space-y-3">
      <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
      <span className="text-sm text-slate-500">A carregar...</span>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, mustChangePassword } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to change password if required (except if already on that page)
  if (mustChangePassword && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );
};

// Protected route for QC app - redirects to /qc-app instead of /login
const QCProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/qc-app" replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors offset={70} closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route 
            path="/reset-password" 
            element={
              <Suspense fallback={<PageLoader />}>
                <ResetPassword />
              </Suspense>
            } 
          />
          <Route 
            path="/qc-app" 
            element={
              <Suspense fallback={<PageLoader />}>
                <QCLogin />
              </Suspense>
            } 
          />
          <Route 
            path="/change-password" 
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning-overview"
            element={
              <ProtectedRoute>
                <PlanningOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute>
                <ProjectForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <ProtectedRoute>
                <ProjectForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production"
            element={
              <ProtectedRoute>
                <Production />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stages"
            element={
              <ProtectedRoute>
                <Stages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partners"
            element={
              <ProtectedRoute>
                <Partners />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <Suppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-types"
            element={
              <ProtectedRoute>
                <OrderTypes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkpoints"
            element={
              <ProtectedRoute>
                <Checkpoints />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rules"
            element={
              <ProtectedRoute>
                <Rules />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <ProtectedRoute>
                <Alerts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <RuleExecutionLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/listings"
            element={
              <ProtectedRoute>
                <CustomListings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <Planning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/production-planning"
            element={
              <ProtectedRoute>
                <ProductionPlanning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/capacity"
            element={
              <ProtectedRoute>
                <Capacity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands"
            element={
              <ProtectedRoute>
                <Brands />
              </ProtectedRoute>
            }
          />
          <Route
            path="/confeccao-planning"
            element={
              <ProtectedRoute>
                <ConfeccaoPlanning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservas"
            element={
              <ProtectedRoute>
                <Reservas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project-simulation/:projectId"
            element={
              <ProtectedRoute>
                <ProjectSimulation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/confeccao-performance"
            element={
              <ProtectedRoute>
                <ConfeccaoPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lavandaria"
            element={
              <ProtectedRoute>
                <Lavandaria />
              </ProtectedRoute>
            }
          />
          <Route
            path="/acabamentos-planning"
            element={
              <ProtectedRoute>
                <AcabamentosPlanning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/acabamentos-performance"
            element={
              <ProtectedRoute>
                <AcabamentosPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/acabamentos-capacity"
            element={
              <ProtectedRoute>
                <AcabamentosCapacity />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quality-control"
            element={
              <ProtectedRoute>
                <QualityControlDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cutting-simulator"
            element={
              <ProtectedRoute>
                <CuttingSimulator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/confeccao"
            element={
              <ProtectedRoute>
                <ReportsConfeccao />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/acabamentos"
            element={
              <ProtectedRoute>
                <ReportsAcabamentos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <ProtectedRoute>
                <Permissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/operator"
            element={
              <ProtectedRoute>
                <OperatorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/acabamentos-simulation/:projectId"
            element={
              <ProtectedRoute>
                <AcabamentosSimulation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/view/:viewId"
            element={
              <ProtectedRoute>
                <ListView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agenda"
            element={
              <ProtectedRoute>
                <Agenda />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/fields"
            element={
              <ProtectedRoute>
                <FieldsConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qc/:projectId"
            element={
              <QCProtectedRoute>
                <QualityControlMobile />
              </QCProtectedRoute>
            }
          />
          <Route
            path="/settings/email"
            element={
              <ProtectedRoute>
                <EmailSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
